import * as R from 'ramda';
import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import { Alert, Col, Input, Row } from 'reactstrap';
import { AddressbookMetadata } from '../../../functions/src/services/infra/dotdigital';
import { DDAccountSelector } from '../../components/DDAccountSelector';
import { DDAddressbookSelector } from '../../components/DDAddressbookSelector';
import { logger } from '../../logging';
import { getDAMApp } from '../../services/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ref } from '../../utils/firebase';
import { equalTo, get, orderByChild, query } from 'firebase/database';
import { DamAccountLink } from './DamAccountLink';
import { StackedField } from './WorkflowFormFields';

const workflowsStyles = require('./workflows.css');

export const ContactsMigrationArgs = ({
  initialArgs,
  formId,
  setFormId,
  setWorkflowArgs,
  setWorkflowArgsValid,
  setDestAccType,
  disabled,
  formPublished = null,
}: {
  initialArgs: any;
  formId: string;
  setFormId: (id: string) => void;
  setWorkflowArgs: (args: any) => void;
  setWorkflowArgsValid: (valid: boolean) => void;
  setDestAccType: (accountType: any) => void;
  disabled?: boolean;
  /** `true` when selected IFB form is published; `false` unpublished; `null` unknown/loading/none */
  formPublished?: boolean | null;
}) => {
  const damApp = getDAMApp();
  const functions = getFunctions(damApp);
  const [sourceAccountId, setSourceAccountId] = useState(null);
  const [sourceAddressbookId, setSourceAddressbookId] = useState(null);
  const [destinationAccountId, setDestinationAccountId] = useState(null);
  const [destinationAddressbookId, setDestinationAddressbookId] =
    useState(null);
  const [errorMessages, setErrorMessages] = useState({});
  const [sourceContactsCount, setSourceContactsCount] = useState(null);
  const [destinationContactsCount, setDestinationContactsCount] =
    useState(null);
  const [sourceAccountValid, setSourceAccountValid] = useState(true);
  const [destinationAccountValid, setDestinationAccountValid] = useState(true);
  const [isPrivacyWorkflow, setIsPrivacyWorkflow] = useState(false);
  const [isStoreWorkflow, setIsStoreWorkflow] = useState(false);
  const hydratedArgsKeyRef = useRef<string | null>(null);
  const lastDerivedArgsRef = useRef<string | null>(null);
  const lastEmittedArgsRef = useRef<string | null>(null);
  const lastValidRef = useRef<boolean | null>(null);

  // Hydrate local state once per workflow open — not on every parent args merge
  useEffect(() => {
    if (initialArgs == null) {
      hydratedArgsKeyRef.current = null;
      return;
    }

    const hydrationKey = JSON.stringify(initialArgs);
    if (hydratedArgsKeyRef.current === hydrationKey) {
      return;
    }

    hydratedArgsKeyRef.current = hydrationKey;
    lastEmittedArgsRef.current = null;
    lastValidRef.current = null;
    lastDerivedArgsRef.current = null;
    setSourceAccountId(initialArgs.source_account || null);
    setSourceAddressbookId(initialArgs.source_addressbook || null);
    setDestinationAccountId(initialArgs.destination_account || null);
    setDestinationAddressbookId(initialArgs.destination_addressbook || null);
    setFormId(initialArgs.form_id || '');
    setIsPrivacyWorkflow(initialArgs.is_privacy || false);
    setIsStoreWorkflow(initialArgs.is_store || false);

    const hasCompleteArgs = Boolean(
      initialArgs.source_account &&
        initialArgs.source_addressbook &&
        initialArgs.destination_account &&
        initialArgs.destination_addressbook &&
        initialArgs.form_id &&
        formPublished === true
    );
    if (hasCompleteArgs) {
      lastValidRef.current = true;
      setWorkflowArgsValid(true);
    }
  }, [initialArgs, setFormId, setWorkflowArgsValid, formPublished]);

  useEffect(() => {
    const derivedArgs = {
      is_privacy: isPrivacyWorkflow,
      is_store: isStoreWorkflow,
      form_id: formId,
    };
    const serialized = JSON.stringify(derivedArgs);
    if (lastDerivedArgsRef.current === serialized) {
      return;
    }
    lastDerivedArgsRef.current = serialized;
    setWorkflowArgs(derivedArgs);
  }, [isPrivacyWorkflow, isStoreWorkflow, formId, setWorkflowArgs]);

  useEffect(() => {
    let isMounted = true;
    const newMessage: any = {};
    const isSameAddressbook =
      `${sourceAccountId}${sourceAddressbookId}` ===
      `${destinationAccountId}${destinationAddressbookId}`;
    if (isSameAddressbook && sourceAccountId && sourceAddressbookId) {
      newMessage['isSameAddressbook'] = {
        message:
          "You can't have the same addressbook for both source and destination",
      };
    } else {
      delete newMessage.isSameAddressbook;
    }

    if (sourceAccountId && sourceAccountValid === false) {
      newMessage['sourceAccountError'] = {
        message: 'No IFB creds for source account',
      };
    } else {
      delete newMessage.sourceAccountError;
    }

    if (destinationAccountId && destinationAccountValid === false) {
      newMessage['destinationAccountError'] = {
        message: 'No IFB creds for destination account',
      };
    } else {
      delete newMessage.destinationAccountError;
    }

    if (formId && formPublished === false) {
      newMessage['formUnpublishedError'] = {
        message:
          'IFB form is not published. Publish it in IFB or choose a published form.',
      };
    } else {
      delete newMessage.formUnpublishedError;
    }

    setErrorMessages(newMessage);

    const isValid =
      Boolean(
        sourceAccountId &&
          sourceAddressbookId &&
          destinationAccountId &&
          destinationAddressbookId &&
          formId &&
          !isSameAddressbook &&
          sourceAccountValid &&
          destinationAccountValid &&
          formPublished === true
      );

    if (isValid) {
      const nextArgs = {
        source_account: parseInt(sourceAccountId, 10),
        source_addressbook: parseInt(sourceAddressbookId, 10),
        destination_account: parseInt(destinationAccountId, 10),
        destination_addressbook: parseInt(destinationAddressbookId, 10),
        form_id: formId,
        is_privacy: isPrivacyWorkflow,
        is_store: isStoreWorkflow,
      };
      const serialized = JSON.stringify(nextArgs);

      if (isMounted && lastEmittedArgsRef.current !== serialized) {
        lastEmittedArgsRef.current = serialized;
        setWorkflowArgs(nextArgs);
      }
      if (isMounted && lastValidRef.current !== true) {
        lastValidRef.current = true;
        setWorkflowArgsValid(true);
        setErrorMessages({});
      }
    } else if (isMounted) {
      const isHydrating =
        initialArgs != null &&
        initialArgs.source_account &&
        !sourceAccountId;

      if (!isHydrating && lastValidRef.current !== false) {
        lastValidRef.current = false;
        setWorkflowArgsValid(false);
      }
    }

    return () => {
      isMounted = false;
    };
  }, [
    sourceAccountId,
    sourceAddressbookId,
    destinationAccountId,
    destinationAddressbookId,
    formId,
    isPrivacyWorkflow,
    isStoreWorkflow,
    sourceAccountValid,
    destinationAccountValid,
    formPublished,
    setWorkflowArgs,
    setWorkflowArgsValid,
    initialArgs,
  ]);

  useEffect(() => {
    let isMounted = true;

    if (isMounted) {
      if (!sourceAddressbookId) {
        setSourceContactsCount(null);
        return;
      }

      setSourceAddressbookContactsCount(sourceAccountId, sourceAddressbookId);
    }

    return () => {
      isMounted = false;
    };
  }, [sourceAddressbookId]);

  useEffect(() => {
    let isMounted = true;

    if (isMounted) {
      if (!destinationAddressbookId) {
        setDestinationContactsCount(null);
        return;
      }

      setDestinationAddressbookContactsCount(
        destinationAccountId,
        destinationAddressbookId
      );
    }

    return () => {
      isMounted = false;
    };
  }, [sourceAddressbookId]);

  const setSourceAddressbookContactsCount = async (
    accountId,
    addressbookId
  ) => {
    const contacts = await getAddressbooksCount(accountId, addressbookId);
    setSourceContactsCount(contacts);
  };

  const setDestinationAddressbookContactsCount = async (
    accountId,
    addressbookId
  ) => {
    const contacts = await getAddressbooksCount(accountId, addressbookId);
    setDestinationContactsCount(contacts);
  };

  const getAddressbooksCount = async (accountId, addressbookId) => {
    try {
      logger.info(
        `[getAddressbooksCount] calling accountManager-callableGetAddressbook`
      );
      const callable = httpsCallable(
        functions,
        'accountManager-callableGetAddressbook'
      );
      const res = await callable({ accountId, addressbookId });
      const addressbook = res.data as AddressbookMetadata;
      return addressbook.contacts;
    } catch (e) {
      logger.error(`[getAddressbooksCount] error: ${e}`);
      return null;
    }
  };

  const isAccountValid = async (id) => {
    logger.debug(`[isAccountValid] called for id: ${id}`);
    let valid = false;
    try {
      const ddAccountRef = ref(`accounts/dotdigital`);
      const ddAccountQuery = query(
        ddAccountRef,
        orderByChild('ddAccountId'),
        equalTo(id)
      );
      const ddAccountSnap = await get(ddAccountQuery);
      let ddAccountKey = '';
      ddAccountSnap.forEach((snap) => {
        ddAccountKey = snap.key;
      });

      logger.debug(`key: ${ddAccountKey}`);
      const credentialsRef = ref(`credentials/${ddAccountKey}`);
      const credentialsSnap = await get(credentialsRef);
      const count = credentialsSnap.size;
      if (count > 0) {
        credentialsSnap.forEach((snap) => {
          if (snap.val().name === 'form-builder') {
            logger.debug(`cred.name: ${snap.val().name}`);
            valid = true;
          }
        });
      }
    } catch (e) {
      logger.error(`[isAccountValid] error: ${e}`);
    }

    return valid;
  };

  const isWorkflowPrivacy = async (sourceId, destId) => {
    logger.debug(
      `[isWorkflowPrivacy] called for account ids: ${sourceId} and ${destId}`
    );
    let isPrivacy = false;
    // check sourceAccount
    try {
      const ddAccountSourceRef = ref(`accounts/dotdigital`);
      const ddAccountSourceQuery = query(
        ddAccountSourceRef,
        orderByChild('ddAccountId'),
        equalTo(`${sourceId}`)
      );
      const ddAccountSourceSnap = await get(ddAccountSourceQuery);

      ddAccountSourceSnap.forEach((snap) => {
        if (snap.val().ddAccountType === 'exile') {
          logger.debug(
            `[isWorkflowPrivacy] returns true for source account: ${sourceId}`
          );
          isPrivacy = true;
        }
      });
    } catch (e) {
      logger.error(
        `[isWorkflowPrivacy] error checking source account type: ${e}`
      );
    }

    // check destinationAccount
    try {
      const ddAccountDestRef = ref(`accounts/dotdigital`);
      const ddAccountDestQuery = query(
        ddAccountDestRef,
        orderByChild('ddAccountId'),
        equalTo(`${destId}`)
      );
      const ddAccountDestSnap = await get(ddAccountDestQuery);

      ddAccountDestSnap.forEach((snap) => {
        if (snap.val().ddAccountType === 'exile') {
          logger.debug(
            `[isWorkflowPrivacy] returns true for dest account: ${destId}`
          );
          isPrivacy = true;
        }
      });
    } catch (e) {
      logger.error(
        `[isAccountValid] error checking destination account type: ${e}`
      );
    }

    return isPrivacy;
  };

  const getAccountType = async (accountId: string) => {
    // check destinationAccount
    try {
      const ddAccountRef = ref(`accounts/dotdigital`);
      const ddAccountQuery = query(
        ddAccountRef,
        orderByChild('ddAccountId'),
        equalTo(`${accountId}`)
      );
      const ddAccountSnap = await get(ddAccountQuery);

      let account;
      ddAccountSnap.forEach((snap) => {
        account = snap.val();
      });

      return account.ddAccountType;
    } catch (e) {
      logger.error(e, `[getAccountType] error fetching account`);
    }
  };

  const isWorkflowStore = async (sourceId) => {
    logger.debug(`[isWorkflowStore] called for source account id: ${sourceId}`);
    let isStore = false;
    // check sourceAccount
    try {
      const ddAccountSourceRef = ref(`accounts/dotdigital`);
      const ddAccountSourceQuery = query(
        ddAccountSourceRef,
        orderByChild('ddAccountId'),
        equalTo(`${sourceId}`)
      );
      const ddAccountSourceSnap = await get(ddAccountSourceQuery);

      ddAccountSourceSnap.forEach((snap) => {
        if (snap.val().ddAccountType === 'store') {
          logger.debug(
            `[isWorkflowStore] returns true for source account: ${sourceId}`
          );
          isStore = true;
        }
      });
    } catch (e) {
      logger.error(
        `[isWorkflowStore] error checking source account type: ${e}`
      );
    }

    return isStore;
  };

  const handleSourceAccountChange = async (id) => {
    logger.debug(`handleSourceAccountChanged: id: ${id}`);
    // check if accountId valid (has IFB creds)
    const accountValid = await isAccountValid(id);
    logger.debug(`handleSourceAccountChanged: accountValid: ${accountValid}`);
    setSourceAccountValid(accountValid);
    setSourceAccountId(id);
    setSourceAddressbookId(null);

    // check if either are exile (privacy)
    const isPrivacy = await isWorkflowPrivacy(id, destinationAccountId);
    logger.debug(
      `[handleSourceAccountChange] setting isPrivacyWorkflow to: ${isPrivacy}`
    );
    setIsPrivacyWorkflow(isPrivacy);

    // check if source is store
    const isStore = await isWorkflowStore(id);
    logger.debug(
      `[handleSourceAccountChange] setting isStoreWorkflow to: ${isStore}`
    );
    setIsStoreWorkflow(isStore);
  };

  const handleDestinationAccountChange = async (id) => {
    logger.debug(`handleDestinationAccountChanged: id: ${id}`);
    const accountValid = await isAccountValid(id);
    const accountType = await getAccountType(id);
    const isPrivacy = await isWorkflowPrivacy(sourceAccountId, id);

    logger.debug(
      `[handleDestinationAccountChange] setting isPrivacyWorkflow to: ${isPrivacy}`
    );
    logger.debug(
      `[handleDestinationAccountChange] dest acc type ${accountType}`
    );

    setDestinationAccountValid(accountValid);
    setDestinationAccountId(id);
    setDestinationAddressbookId(null);
    setIsPrivacyWorkflow(isPrivacy);
    setDestAccType(accountType);
  };

  const accountFieldLabel = (
    text: string,
    accountId: string | number | null
  ) =>
    accountId ? (
      <DamAccountLink ddAccountId={accountId}>{text}</DamAccountLink>
    ) : (
      text
    );

  return (
    <div className={workflowsStyles.sectionCard}>
      <div className={workflowsStyles.sectionHead}>
        <p className={workflowsStyles.sectionTitle}>
          Contacts migration options
        </p>
        <p className={workflowsStyles.sectionCaption}>
          Select and verify source and destination accounts and related
          address books.
        </p>
      </div>
      <div className={workflowsStyles.sectionBody}>
        {errorMessages && Object.keys(errorMessages).length > 0
          ? R.map((errorMessage: any) => {
              return (
                <Row key={errorMessage}>
                  <Col sm={12}>
                    <Alert color="danger">
                      {errorMessage && errorMessages[errorMessage].message}
                    </Alert>
                  </Col>
                </Row>
              );
            }, Object.keys(errorMessages))
          : null}
        <Row>
          <Col sm={6}>
            <StackedField
              label={accountFieldLabel('Source DD Account', sourceAccountId)}
              required
              htmlFor="sourceAccount"
            >
              <DDAccountSelector
                setAccountId={handleSourceAccountChange}
                accountId={sourceAccountId}
                disabled={disabled}
              />
            </StackedField>
            <StackedField
              label="Source DD Addressbook"
              required
              htmlFor="sourceAddressbook"
            >
              <DDAddressbookSelector
                setAddressbookId={setSourceAddressbookId}
                addressbookId={sourceAddressbookId}
                accountId={sourceAccountId}
                disabled={disabled}
                excludeNonConsented={false}
              />
            </StackedField>
            <StackedField
              label={accountFieldLabel(
                'Destination DD Account',
                destinationAccountId
              )}
              required
              htmlFor="destinationAccount"
            >
              <DDAccountSelector
                setAccountId={handleDestinationAccountChange}
                accountId={destinationAccountId}
                disabled={disabled}
              />
            </StackedField>
            <StackedField
              label="Destination DD Addressbook"
              required
              htmlFor="destinationAddressbook"
            >
              <DDAddressbookSelector
                setAddressbookId={setDestinationAddressbookId}
                addressbookId={destinationAddressbookId}
                accountId={destinationAccountId}
                disabled={disabled}
                excludeNonConsented={true}
              />
            </StackedField>
          </Col>
          <Col sm={6}>
            <StackedField
              label={accountFieldLabel(
                'Source DD Account ID',
                sourceAccountId
              )}
              htmlFor="sourceAccountId"
            >
              <Input
                type="text"
                name="sourceAccountId"
                id="sourceAccountId"
                value={(sourceAccountId && sourceAccountId) || ''}
                disabled={true}
              />
            </StackedField>
            <StackedField
              label="Source DD Addressbook ID"
              htmlFor="sourceAddressbookId"
            >
              <Input
                type="text"
                name="sourceAddressbookId"
                id="sourceAddressbookId"
                value={(sourceAddressbookId && sourceAddressbookId) || ''}
                disabled={true}
              />
              {sourceAddressbookId &&
              (sourceContactsCount || sourceContactsCount === 0) ? (
                <span
                  className={workflowsStyles.contactsCount}
                >{`Addressbook contacts count = ${sourceContactsCount}`}</span>
              ) : null}
            </StackedField>
            <StackedField
              label={accountFieldLabel(
                'Destination DD Account ID',
                destinationAccountId
              )}
              htmlFor="destinationAccountId"
            >
              <Input
                type="text"
                name="destinationAccountId"
                id="destinationAccountId"
                value={(destinationAccountId && destinationAccountId) || ''}
                disabled={true}
              />
            </StackedField>
            <StackedField
              label="Destination DD Addressbook ID"
              htmlFor="destinationAddressbookId"
            >
              <Input
                type="text"
                name="destinationAddressbookId"
                id="destinationAddressbookId"
                value={
                  (destinationAddressbookId && destinationAddressbookId) || ''
                }
                disabled={true}
              />
              {destinationAddressbookId &&
              (destinationContactsCount || destinationContactsCount === 0) ? (
                <span
                  className={workflowsStyles.contactsCount}
                >{`Addressbook contacts count = ${destinationContactsCount}`}</span>
              ) : null}
            </StackedField>
          </Col>
        </Row>
      </div>
    </div>
  );
};
